import React, { useCallback, useMemo, useRef } from 'react';
import { ConfigProvider, Modal as AntdModal, ModalProps } from 'antd';
import defaultsDeep from 'lodash/defaultsDeep';

import { useTheme } from 'styled-components';
import { buildNextModalTheme } from '@ui-library/next/tokens';

import { NextConfirmFooter, NextFooter } from './footer';

type ModalVariant = 'next';

interface ModalV2Props extends ModalProps {
  variant?: ModalVariant;
}

export const useModal = (options?: {
  variant?: ModalVariant;
}): ReturnType<typeof AntdModal.useModal> => {
  const { token } = useTheme();
  const nextModalTheme = useMemo(() => buildNextModalTheme(token.next), [token.next]);
  const [modal, modalInstance] = AntdModal.useModal();
  const originalConfirm = modal.confirm;
  const destroyRef = useRef<() => void>();

  const isNext = options?.variant === 'next';

  const defaultProps = {
    icon: null,
    centered: true,
    closable: true,
    maskClosable: true,
    className: 'md_modal',
    modalRender: (node) => (
      <div data-testid="customModal" className={isNext ? 'ant-modal-next-content-wrapper' : ''}>
        {node}
      </div>
    ),
    okButtonProps: {
      'data-testId': 'btnSave',
    },
    cancelButtonProps: {
      'data-testId': 'btnCancel',
    },
  } as ModalProps;

  modal.confirm = useCallback((props) => {
    const mergedProps = defaultsDeep({}, props, defaultProps);

    if (isNext) {
      const footer = (
        <div className="ant-model-next-confirm-footer">
          {mergedProps.footer === undefined ? (
            <NextConfirmFooter
              onOk={mergedProps.onOk}
              onCancel={mergedProps.onCancel}
              okText={mergedProps.okText}
              cancelText={mergedProps.cancelText}
              okButtonProps={mergedProps.okButtonProps}
              cancelButtonProps={mergedProps.cancelButtonProps}
              destroyRef={destroyRef}
            />
          ) : (
            mergedProps.footer
          )}
        </div>
      );

      const result = originalConfirm({ ...mergedProps, footer });
      destroyRef.current = result?.destroy;
      return result;
    }

    return originalConfirm(mergedProps);
  }, []);

  const instance = (
    // When the modal is triggered from inside a table cell, clicks on modal are being propagated and the table' onRow click is triggered
    <div style={{ display: 'none' }} onClick={(e) => e.stopPropagation()}>
      {modalInstance}
    </div>
  );

  return [
    modal,
    options?.variant === 'next' ? (
      <ConfigProvider theme={nextModalTheme}>{instance}</ConfigProvider>
    ) : (
      instance
    ),
  ];
};

export const Modal: React.FC<ModalV2Props> = ({ variant, ...props }) => {
  const { token } = useTheme();
  const nextModalTheme = useMemo(() => buildNextModalTheme(token.next), [token.next]);
  const isNext = variant === 'next';

  // When variant="next", replace Ant's default footer with ui-library buttons
  const nextFooter =
    isNext && props.footer === undefined ? (
      <NextFooter
        onOk={props.onOk}
        onCancel={props.onCancel}
        okText={props.okText}
        cancelText={props.cancelText}
        okButtonProps={props.okButtonProps}
        cancelButtonProps={props.cancelButtonProps}
        confirmLoading={props.confirmLoading}
      />
    ) : undefined;

  const modal = (
    // When the modal is triggered from inside a table cell, clicks on modal are being propagated and the table' onRow click is triggered
    <div style={{ display: 'none' }} onClick={(e) => e.stopPropagation()}>
      <AntdModal
        {...props}
        data-testid="customModal"
        okButtonProps={{ ...props.okButtonProps, 'data-testId': 'btnSave' }}
        cancelButtonProps={{ ...props.cancelButtonProps, 'data-testId': 'btnCancel' }}
        {...(nextFooter !== undefined ? { footer: nextFooter } : {})}
        className={`${isNext ? 'ant-modal-next' : ''} ${props.className ?? ''}`}
        centered
      />
    </div>
  );

  if (isNext) {
    return <ConfigProvider theme={nextModalTheme}>{modal}</ConfigProvider>;
  }

  return modal;
};
